import { createSlice } from '@reduxjs/toolkit';
import { HandleSearchResponsePayload } from '@/types/slices/searchSlicePayloads';
import { SlicePayload } from '@/types/slices/slicePayload';
import { LocationState } from '@/store/slices/locationSlice';
import { LocationDetails } from '@/types/locationDetails';
import { loadRecentLocations, saveRecentLocations } from '@/utils/recentLocationsStorage';

const MAX_RECENT_LOCATIONS = 5;

export interface SearchState {
  search: SearchStateSlice;
}

export type SearchResultMode = 'replace' | 'append';
export type SortBy = 'bestMatch' | 'nearest' | 'highestRated' | 'alphabetical' | 'alphabetical_desc';
export type PageView = 'search' | 'map';
export type CompatibilityFilter = 'all' | 'none' | 'low' | 'some' | 'medium' | 'high' | 'highest';
export type DistanceRange = 'any' | '0.25mi' | '0.5mi' | '1mi' | '3mi' | '5mi';

export interface SearchFilters {
  compatibilityLevel: CompatibilityFilter;
  openNow: boolean;
  notVisited: boolean;
  distanceRange: DistanceRange;
}

interface SearchStateSlice {
  searchTerm: string;
  placeType: string;
  pages: string;
  locationPlaceId: string;
  displayLocation: string;
  recentLocations: Record<string, LocationDetails>;
  isSearchLoading: boolean;
  searchResultMode: SearchResultMode;
  sortBy: SortBy;
  filters: SearchFilters;
  pageView: PageView;
}

const defaultFilters: SearchFilters = {
  compatibilityLevel: 'all',
  openNow: false,
  notVisited: false,
  distanceRange: 'any',
};

const initialState: SearchStateSlice = {
  searchTerm: '',
  placeType: 'restaurant',
  pages: '1',
  locationPlaceId: '',
  displayLocation: '',
  recentLocations: loadRecentLocations(),
  isSearchLoading: false,
  searchResultMode: 'replace',
  sortBy: 'bestMatch',
  filters: { ...defaultFilters },
  pageView: 'search',
}

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchTerm: (state, action: SlicePayload) => {
      state.searchTerm = action.payload;
    },
    setPlaceType: (state, action: SlicePayload) => {
      state.placeType = action.payload;
    },
    setPages: (state, action: SlicePayload) => {
      state.pages = action.payload;
    },
    setDisplayLocation: (state, action: SlicePayload) => {
      state.displayLocation = action.payload;
    },
    setLocationPlaceId: (state, action: SlicePayload) => {
      state.locationPlaceId = action.payload;
    },
    setRecentLocation: (state, action: SlicePayload) => {
      const placeId = action.payload?.placeId;
      if (!placeId || state.recentLocations[placeId]) return;

      const keys = Object.keys(state.recentLocations);
      if (keys.length >= MAX_RECENT_LOCATIONS) {
        delete state.recentLocations[keys[0]];
      }

      state.recentLocations[placeId] = action.payload;
      // Persist to localStorage
      saveRecentLocations(state.recentLocations);
    },
    setIsSearchLoading: (state, action: SlicePayload) => {
      state.isSearchLoading = action.payload;
    },
    setSearchResultMode: (state, action: SlicePayload) => {
      state.searchResultMode = action.payload;
    },
    setSortBy: (state, action: SlicePayload<SortBy>) => {
      state.sortBy = action.payload;
    },
    setFilters: (state, action: SlicePayload<Partial<SearchFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = { ...defaultFilters };
      state.sortBy = 'bestMatch';
    },
    setPageView: (state, action: SlicePayload<PageView>) => {
      state.pageView = action.payload;
    },
    handleSearchResponse: (state, action: HandleSearchResponsePayload) => {
      state.displayLocation = action.payload.location;

      // Only save to recent locations if we have a valid placeId
      const placeId = action.payload.placeId;
      if (placeId && !state.recentLocations[placeId]) {
        const keys = Object.keys(state.recentLocations);
        if (keys.length >= MAX_RECENT_LOCATIONS) {
          delete state.recentLocations[keys[0]];
        }
        state.recentLocations[placeId] = action.payload;
        // Persist to localStorage
        saveRecentLocations(state.recentLocations);
      }
    },
  },
});

export const searchReducer = searchSlice.reducer;
export const {
  setSearchTerm,
  setPlaceType,
  setPages,
  setDisplayLocation,
  setLocationPlaceId,
  setRecentLocation,
  setIsSearchLoading,
  setSearchResultMode,
  setSortBy,
  setFilters,
  resetFilters,
  handleSearchResponse,
  setPageView,
} = searchSlice.actions;

export const selectIsSearchModified = (state: SearchState) =>
  state.search.searchTerm !== initialState.searchTerm ||
  state.search.placeType !== initialState.placeType ||
  state.search.pages !== initialState.pages ||
  state.search.sortBy !== initialState.sortBy ||
  state.search.filters.compatibilityLevel !== defaultFilters.compatibilityLevel ||
  state.search.filters.openNow !== defaultFilters.openNow ||
  state.search.filters.notVisited !== defaultFilters.notVisited ||
  state.search.filters.distanceRange !== defaultFilters.distanceRange;

export const selectSortBy = (state: SearchState) => state.search.sortBy;
export const selectFilters = (state: SearchState) => state.search.filters;
export const selectPageView = (state: SearchState) => state.search.pageView;